import { clsx } from "@/lib/clsx";
import { Container } from "@/components/site/Container";

/** A page section. `tone` sets the background for intentional rhythm — not
 *  every section looks the same (breaking the metronomic "AI" cadence). */
export function Section({
  children,
  tone = "paper",
  id,
  className,
  bordered = true,
}: {
  children: React.ReactNode;
  tone?: "paper" | "paper2" | "ink";
  id?: string;
  className?: string;
  bordered?: boolean;
}) {
  const tones = {
    paper: "bg-paper text-ink",
    paper2: "bg-paper-2 text-ink",
    ink: "bg-ink text-white",
  };
  return (
    <section
      id={id}
      className={clsx(
        "py-[72px]",
        tones[tone],
        bordered && tone !== "ink" && "border-b border-line",
        className,
      )}
    >
      <Container>{children}</Container>
    </section>
  );
}

export function Eyebrow({
  children,
  onDark = false,
}: {
  children: React.ReactNode;
  onDark?: boolean;
}) {
  return (
    <span
      className={clsx(
        "font-mono text-xs font-medium uppercase tracking-eyebrow",
        onDark ? "text-brass-soft" : "text-brass-deep",
      )}
    >
      {children}
    </span>
  );
}

export function SectionHeading({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h2
      className={clsx(
        "mt-3.5 font-display text-[clamp(26px,3vw,34px)] font-semibold",
        className,
      )}
    >
      {children}
    </h2>
  );
}

/**
 * A designed slot for imagery/portraits we don't have yet — deliberately NOT a
 * dashed stock box (that placeholder look is one of the "AI/unfinished" tells).
 * Reads as an intentional brand panel with a quiet lever monogram.
 */
export function FieldPlaceholder({
  label,
  ratio = "16/5",
  tone = "ink",
  className,
}: {
  label: string;
  ratio?: string;
  tone?: "ink" | "paper2";
  className?: string;
}) {
  return (
    <div
      className={clsx(
        "relative flex items-end overflow-hidden rounded-sm",
        tone === "ink" ? "bg-ink text-paper" : "bg-paper-2 text-ink-60",
        className,
      )}
      style={{ aspectRatio: ratio }}
    >
      {/* quiet graticule so the empty slot still feels considered */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.14]"
        style={{
          backgroundImage:
            "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
          backgroundSize: "34px 34px",
        }}
      />
      <span className="relative z-10 p-5 font-mono text-[11px] uppercase tracking-[0.1em] opacity-80">
        {label}
      </span>
    </div>
  );
}
